import { useEffect, useState } from "react";
import { GenericTable } from "../GenericTable/GenericTable";
import { IUserMigrationWaitingVO } from "../../../../../model/UserModel";
import { acceptedMigrateSigntureUser, allUsersMigrationWaiting, migrateUserSigntureDeclined } from "../../../../../services/OrganizationServices";
import { useParams } from "react-router-dom";
import { AlertColor, Avatar, Box, CircularProgress, Container, TableCell, TableRow, Typography } from "@mui/material";
import { NotificationImportant, NotificationsActive } from "@mui/icons-material";
import { KeyDriveBodyMin } from "../../../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMin";
import { EmptyContainer } from "../../../../../components/KeydriveComponents/EmptyContainer/EmptyContainer";
import { KeyDrivePrimaryButton } from "../../../../../components/KeydriveComponents/Buttons/KeyDrivePrimaryButton";
import { IAcceptMigrationUser, IDeclineMigrationUser } from "../../../../../model/OrganizationModel";
import { useTranslation } from "react-i18next";
import KeydriveSnackbar from "../../../../../components/KeydriveComponents/Snackbar/KeydriveSnackbar";

export interface IMigrationNotificationRow {
    migrationUser: IUserMigrationWaitingVO;
    setRefreshMigrationList: (set: boolean) => void;
    fetchAllNotifications: () => Promise<void>;
    setMessageSnack: (set: string | undefined) => void;
    setColorSnack: (set: AlertColor) => void;
}

export default function NotificationGuestTable() {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const MIGRATIONWAITING_PER_PAGE = 10;
    const [migrationWaitingUsers, setMigrationWaitingUsers] = useState<IUserMigrationWaitingVO[]>([]);
    const { nameOrg } = useParams();
    const [refreshMigrationList, setRefreshMigrationList] = useState<boolean>(false); 
    const { t } = useTranslation();
    const [messageSnack, setMessageSnack] = useState<string | undefined>(undefined);
    const [colorSnack, setColorSnack] = useState<AlertColor | undefined>();

    useEffect(() => {
        fetchAllMigrationNotification();
    }, []);

    async function fetchAllMigrationNotification() {
        setRefreshMigrationList(true);
        try {
            if (nameOrg != undefined) {
                const data = await allUsersMigrationWaiting(nameOrg);
                setMigrationWaitingUsers(data);
            }
        } catch(err: any) { 
            setMessageSnack(err.response.data);
            setColorSnack("error");
        } finally {
            setRefreshMigrationList(false);
        }
    }

    const displayMigrations = migrationWaitingUsers.slice((currentPage - 1) * MIGRATIONWAITING_PER_PAGE, currentPage * MIGRATIONWAITING_PER_PAGE);

    if (displayMigrations.length <= 0) {
        return (
            <Box
                height="calc(-370px + 100vh)"
            >
                <EmptyContainer
                    icon={<NotificationImportant />}
                    title={t("MigrationsTab.NotMigrationsPending")}
                    content={t("MigrationsTab.MigrationsPendingContent")}
                />
            </Box>
        )
    }

    return (
        <>     
            { !refreshMigrationList ? (
                <GenericTable 
                    headers={[
                        t("MigrationsTab.NameUser"),
                        t("MigrationsTab.UserEmail"),
                        t("MigrationsTab.IdSubscription"),
                        {title: t("MigrationsTab.Actions"), sx: {textAlign: "center"}},
                    ]}
                    bodyData={displayMigrations.map((migrationUser) => ( 
                        <>
                            { refreshMigrationList ? (
                                <CircularProgress variant="indeterminate" />
                            ) : (
                                <MigrationNotificationRow setColorSnack={setColorSnack} setMessageSnack={setMessageSnack} fetchAllNotifications={fetchAllMigrationNotification} migrationUser={migrationUser} setRefreshMigrationList={setRefreshMigrationList} />
                            ) }
                        </> 
                    ))}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    dataPerPage={MIGRATIONWAITING_PER_PAGE}
                />
            ) : (
                <Box
                    height="calc(-370px + 100vh)"
                >
                    <CircularProgress variant="indeterminate" />
                </Box>
            ) }
            <KeydriveSnackbar 
                message={messageSnack}
                open={messageSnack !== undefined}
                variant={colorSnack}
                onClose={() => setMessageSnack(undefined)}
            />
        </>
    )
}

function MigrationNotificationRow({
    migrationUser,
    setRefreshMigrationList,
    fetchAllNotifications,
    setMessageSnack,
    setColorSnack
}: IMigrationNotificationRow) {

    const { nameOrg } = useParams();
    const { t } = useTranslation();

    async function acceptMigrationAsync(input: IAcceptMigrationUser) {
        setRefreshMigrationList(true);
        try {
            await acceptedMigrateSigntureUser(input);
            await fetchAllNotifications();
        } catch(err: any) { 
            setMessageSnack(err.response.data);
            setColorSnack("error");
        } finally {
            setRefreshMigrationList(false);
        }
    }

    async function declineMigrationAsync(input: IDeclineMigrationUser) {
        setRefreshMigrationList(true);
        try {
            await migrateUserSigntureDeclined(input);
            await fetchAllNotifications();
        } catch(err: any) { 
            setMessageSnack(err.response.data);
            setColorSnack("error");
        } finally {
            setRefreshMigrationList(false);
        }
    }

    return (
        <TableRow key={migrationUser.signtureId}>
            <TableCell sx={{ width: '30px' }} />
            <TableCell sx={{padding: '8px' }}>
                <Container sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ height: '26px', width: '26px' }} alt={migrationUser.username} />
                    {migrationUser.username}
                </Container>
            </TableCell>
            <TableCell sx={{ padding: '8px' }}>{migrationUser.login}</TableCell>
            <TableCell sx={{ padding: '8px' }}>{migrationUser.signtureId}</TableCell>
            <TableCell sx={{ padding: '8px', display: "flex", alignItems: "center", justifyContent: "center", gap: 3}}>
                <KeyDrivePrimaryButton 
                    TextComponent={KeyDriveBodyMin}
                    TextComponentProps={{
                        color: "#ffffff",
                        needBold: false,
                        needEllipses: false,
                        text: t("MigrationsTab.AceptMigration")
                    }}
                    width="200px"
                    backgroundColor="#22638D"
                    onClick={() => {
                        acceptMigrationAsync({
                            internalName: nameOrg!,
                            userId: migrationUser.userId
                        });
                    }}
                />
                <KeyDrivePrimaryButton 
                    TextComponent={KeyDriveBodyMin}
                    TextComponentProps={{
                        color: "#ffffff",
                        needBold: false,
                        needEllipses: false,
                        text: t("MigrationsTab.DeclineMigration")
                    }}
                    width="200px"
                    backgroundColor="#61a9d8ff"
                    onClick={() => {
                        declineMigrationAsync({
                            internalName: nameOrg!,
                            userId: migrationUser.userId
                        });
                    }}
                />
            </TableCell>
        </TableRow>
    )   
}